import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
} from "firebase/firestore";
import {
  createNamedDocument,
  db,
  deleteDocument,
} from "./firestore.utils";

export const USER_INVITES_COLLECTION = "userInvites";

export const APP_ROLES = {
  ADMINISTRATION: "administration",
  DISPATCHER: "dispatcher",
  TECHNICIAN: "technician",
};

export const APP_ROLE_OPTIONS = [
  { value: APP_ROLES.TECHNICIAN, label: "Technician" },
  { value: APP_ROLES.DISPATCHER, label: "Dispatcher" },
  { value: APP_ROLES.ADMINISTRATION, label: "Administration" },
];

export const ROLE_LABELS = {
  [APP_ROLES.ADMINISTRATION]: "Administration",
  [APP_ROLES.DISPATCHER]: "Dispatcher",
  [APP_ROLES.TECHNICIAN]: "Technician",
};

export const normalizeInviteEmail = (email) => email?.trim().toLowerCase() ?? "";

export const fetchInviteByEmail = async (email) => {
  const inviteId = normalizeInviteEmail(email);
  if (!inviteId) {
    return null;
  }

  const snapshot = await getDoc(doc(db, USER_INVITES_COLLECTION, inviteId));
  if (!snapshot.exists()) {
    return null;
  }

  return { id: snapshot.id, email: snapshot.id, ...snapshot.data() };
};

export const emailHasExistingUser = async (email) => {
  const normalizedEmail = normalizeInviteEmail(email);
  if (!normalizedEmail) {
    return false;
  }

  const snapshot = await getDocs(
    query(collection(db, "users"), where("email", "==", normalizedEmail)),
  );

  return !snapshot.empty;
};

export const createInvite = async ({ displayName, email, role, isActive, createdBy }) => {
  const inviteId = normalizeInviteEmail(email);
  if (!inviteId) {
    throw new Error("Email is required.");
  }

  if (!displayName?.trim()) {
    throw new Error("Display name is required.");
  }

  if (!Object.values(APP_ROLES).includes(role)) {
    throw new Error("Select a valid role.");
  }

  const existingInvite = await fetchInviteByEmail(inviteId);
  if (existingInvite) {
    throw new Error("An invite already exists for this email.");
  }

  const userExists = await emailHasExistingUser(inviteId);
  if (userExists) {
    throw new Error("A user with this email already exists.");
  }

  await createNamedDocument(doc(db, USER_INVITES_COLLECTION, inviteId), {
    createdAt: new Date(),
    createdBy: createdBy || "",
    displayName: displayName.trim(),
    email: inviteId,
    isActive: Boolean(isActive),
    role,
  });
};

export const revokeInvite = async (email) => {
  const inviteId = normalizeInviteEmail(email);
  if (!inviteId) {
    throw new Error("Email is required.");
  }

  await deleteDocument(doc(db, USER_INVITES_COLLECTION, inviteId));
};
